// Consignment: one contract manufacturer (Tailhoo, Elecrow) at a time, picked in the header, with what HHL has there
// in a summary strip and the rest in tabs (Mitch, 2026-11-01: "it's like a dashboard, and it's not so clear when all the
// info for it is on a single scrolling page"): Stock (the parts there, a table with views), Make orders (open builds
// with readiness; finished ones on request), In transit (shipments both ways and supplier POs shipping straight there,
// one table in two bands; Confirm arrived opens next to it) and Counts (due, waiting for review). ?cm= picks the CM, ?tab= the tab. Everything comes from synced rows through
// useCms(), so it's instant.
import { AlertTriangle, Factory, PackageMinus, Plus, Printer, Truck, Undo2 } from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import {
  type CmStockLine,
  type CmSummary,
  COUNT_POLICIES,
  COUNT_POLICY_LABELS,
  type CountPolicy,
  MAKE_STAGE_LABELS,
  type MakeOrderState,
  type ShipmentState,
} from "../../../../shared/consignment.ts";
import { PO_STATUS_LABELS } from "../../../../shared/po.ts";
import { ago, dayText, money, personName, plural, qty } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { navigate, setSearch, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, EmptyState, Select, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../ui/DataTable.tsx";
import { PageSearch } from "../../ui/ListPage.tsx";
import { Figure, PageHeader, type TabItem, Tabs } from "../../ui/PageParts.tsx";
import { openPane, PaneLink, usePaneRoot } from "../../ui/SidePane.tsx";
import { STATUS_TONE, vendorLabel } from "../po/lib.ts";
import { NeededBy } from "../po/NeededBy.tsx";
import { CountDialog, CountReview } from "./Counts.tsx";
import { cmList, dollars, STATUS_LABELS, STATUS_TONES, useCms } from "./lib.ts";
import { ScrapDialog } from "./ScrapDialog.tsx";
import { SetupCmSite } from "./SetupCmSite.tsx";
import { ConfirmArrived, ShipDialog } from "./Shipments.tsx";

type Dialog = "ship" | "return" | "scrap" | "count" | "setup" | null;
const TABS = ["stock", "orders", "transit", "counts"] as const;
type Tab = (typeof TABS)[number];

export function ConsignmentPage() {
  const params = useSearch();
  const cms = useCms();
  const list = cmList(cms);
  // The one asked for, else the CM holding the most.
  const selected = cms.get(params.get("cm") ?? "") ?? [...list].sort((a, b) => b.value.total - a.value.total)[0];
  const tabParam = params.get("tab") as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : "stock";
  const [dialog, setDialog] = useState<Dialog>(params.get("setup") ? "setup" : null);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const canSetup = can("location.make_cm_site");
  const total = list.reduce((n, c) => n + c.value.total, 0);
  // Each table's own search, in its bar (Mitch, 2026-11-04: a table and its controls are one unit)
  const search = (
    <PageSearch
      ref={searchRef}
      value={query}
      onChange={setQuery}
      placeholder={tab === "stock" ? "Filter parts…" : tab === "orders" ? "Filter builds…" : "Filter shipments…"}
      toTable
      className="max-w-64 min-w-32 flex-1 basis-40"
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Consignment"
        actions={
          <>
            {list.length > 1 && (
              <span className="hidden text-[12px] whitespace-nowrap text-faint xl:inline" title="HHL's stock at every CM (balance sheet and insurance)">
                All CMs <span className="font-mono">{dollars(total)}</span>
              </span>
            )}
            {canSetup && (
              // Its words only where there's room: at 1024 px the header has the CM, the views and the search too
              <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} title="Set up a CM site" onClick={() => setDialog("setup")}>
                <span className="hidden xl:inline">Set up a CM site</span>
              </Button>
            )}
          </>
        }
      >
        {/* Which CM: each with what HHL has there, and whether a count is due or waiting. */}
        {list.length > 0 && selected && (
          <div className="w-72 min-w-44 shrink">
            <Select
              aria-label="Contract manufacturer"
              value={selected.location.id}
              onChange={(e) => setSearch({ cm: e.target.value })}
              options={list.map((c) => ({
                value: c.location.id,
                label: `${c.location.name} · ${dollars(c.value.total)}${c.counts.due.due || c.counts.pending.length > 0 ? " · count due" : ""}`,
              }))}
              className="h-7 text-[12px]"
            />
          </div>
        )}
      </PageHeader>

      {!selected ? (
        <div className="min-h-0 flex-1 bg-surface">
          <EmptyState icon={<Factory className="size-8" />} title="No CM sites yet">
            A contract manufacturer's site holds HHL's consigned parts and builds make orders.{" "}
            {canSetup
              ? "Set one up from its Shopify location (e.g. Tailhoo Technology, imported as not used) or as a new site."
              : "Ask an admin to set one up from its Shopify location."}
          </EmptyState>
        </div>
      ) : (
        <CmView key={selected.location.id} cm={selected} tab={tab} query={query} search={search} open={setDialog} />
      )}

      {dialog === "setup" && (
        <SetupCmSite
          preset={params.get("setup") ?? undefined}
          onClose={() => {
            setDialog(null);
            setSearch({ setup: null });
          }}
        />
      )}
      {selected && dialog === "ship" && <ShipDialog cm={selected} direction="to_cm" onClose={() => setDialog(null)} />}
      {selected && dialog === "return" && <ShipDialog cm={selected} direction="to_hhl" onClose={() => setDialog(null)} />}
      {selected && dialog === "scrap" && <ScrapDialog cm={selected} onClose={() => setDialog(null)} />}
      {selected && dialog === "count" && <CountDialog cm={selected} onClose={() => setDialog(null)} />}
    </div>
  );
}

function CmView({ cm, tab, query, search, open }: { cm: CmSummary; tab: Tab; query: string; search: ReactNode; open: (d: Dialog) => void }) {
  const vendor = useRows("vendor").find((v) => v.id === cm.location.vendor_id);
  const openOrders = cm.orders.filter((o) => o.open || o.atCm > 0);
  const building = openOrders.filter((o) => o.open);
  const ready = openOrders.filter((o) => o.status === "ready").length;
  const short = openOrders.filter((o) => o.status === "short").length;
  const editable = can("consign.ship");
  const belowZero = cm.stock.filter((l) => l.onHand < 0).sort((a, b) => a.onHand - b.onHand);
  const stocked = cm.stock.filter((l) => l.onHand !== 0 || l.inTransit !== 0 || l.reserved !== 0);
  const countWaiting = cm.counts.due.due || cm.counts.pending.length > 0;
  const tabs: TabItem<Tab>[] = [
    { key: "stock", label: "Stock", count: stocked.length },
    { key: "orders", label: "Make orders", count: building.length },
    { key: "transit", label: "In transit", count: cm.inTransit.length + cm.inbound.length },
    {
      key: "counts",
      label: "Counts",
      count: cm.counts.pending.length || undefined,
      dot: countWaiting ? { tone: "warn", title: "A count is due or waiting for review" } : undefined,
    },
  ];

  return (
    <>
      {/* The CM: who it is, what HHL has there, and what can be done. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 border-b border-line bg-surface px-4 py-2.5">
        <div className="flex min-w-0 flex-col">
          <span className="text-[15px] font-semibold whitespace-nowrap">{cm.location.name}</span>
          <span className="flex items-center gap-2 text-[12px] whitespace-nowrap">
            {vendor && (
              <PaneLink kind="vendor" id={vendor.id} to={`/vendors?open=${vendor.id}`} className="text-muted hover:underline">
                {vendorLabel(vendor)}
              </PaneLink>
            )}
            {cm.location.shopify_location_id && <Badge tone="accent">Shopify location</Badge>}
          </span>
        </div>
        <Figure
          label="Consigned parts"
          value={money(cm.value.parts)}
          sub={cm.value.inTransit ? `${money(cm.value.inTransit)} in transit` : plural(cm.stock.filter((l) => l.onHand > 0).length, "part")}
        />
        <Figure label="Finished, to ship" value={money(cm.value.finished)} sub={`${qty(openOrders.reduce((n, o) => n + o.atCm, 0)) || "0"} units`} />
        <Figure
          label="Total at risk"
          value={money(cm.value.total)}
          sub={cm.value.missing ? `${cm.value.missing} without a cost` : undefined}
          tone={cm.value.missing ? "warn" : undefined}
        />
        <Figure label="Open builds" value={String(building.length)} sub={`${ready} ready · ${short} short`} tone={short ? "warn" : undefined} />
        {editable && (
          <div className="ml-auto flex flex-wrap gap-1.5">
            <Button size="sm" variant="primary" icon={<Truck className="size-3.5" />} onClick={() => open("ship")}>
              Ship parts
            </Button>
            <Button size="sm" icon={<Undo2 className="size-3.5" />} onClick={() => open("return")}>
              Return to HHL
            </Button>
            <Button size="sm" icon={<PackageMinus className="size-3.5" />} onClick={() => open("scrap")}>
              Scrap
            </Button>
            <Button size="sm" onClick={() => open("count")}>
              Enter count
            </Button>
          </div>
        )}
      </div>

      {/* A part below zero is a count or a shipment HHL Ops hasn't heard about; the value counts it as $0 meanwhile. */}
      {belowZero.length > 0 && (
        <div className="flex shrink-0 items-center gap-3 border-b border-danger/30 bg-danger-soft px-4 py-2 text-[12px]">
          <AlertTriangle className="size-4 shrink-0 text-danger" />
          <span className="min-w-0 flex-1">
            {plural(belowZero.length, "part")} below zero at {cm.location.name}:{" "}
            {belowZero
              .slice(0, 3)
              .map((l) => `${l.item?.sku || l.item?.name || "?"} ${qty(l.onHand).replace("-", "−")}`)
              .join(", ")}
            {belowZero.length > 3 ? ` and ${belowZero.length - 3} more` : ""}.{" "}
            <span className="text-muted">They count as $0 in the value above until a count or a shipment puts them right.</span>
          </span>
          {editable && (
            <Button size="sm" onClick={() => open("count")}>
              Enter count
            </Button>
          )}
        </div>
      )}

      <Tabs tabs={tabs} active={tab} onPick={(t) => setSearch({ tab: t === "stock" ? null : t })} />

      <div className="min-h-0 flex-1 bg-surface">
        {tab === "stock" && <StockTab cm={cm} rows={stocked} query={query} search={search} />}
        {tab === "orders" && <OrdersTab cm={cm} query={query} search={search} />}
        {tab === "transit" && <TransitTab cm={cm} query={query} search={search} />}
        {tab === "counts" && <CountsTab cm={cm} vendorName={vendor ? vendorLabel(vendor) : "the CM"} editable={editable} open={open} />}
      </div>
    </>
  );
}

// Stock -------------------------------------------------------------------------------------------------------

type StockRow = CmStockLine & { id: string };

function StockTab({ cm, rows, query, search }: { cm: CmSummary; rows: CmStockLine[]; query: string; search: ReactNode }) {
  const openItem = usePaneRoot("item");
  const data = useMemo<StockRow[]>(() => rows.map((l) => ({ ...l, id: l.itemId })), [rows]);
  const columns = useMemo<Column<StockRow>[]>(
    () => [
      { key: "photo", header: "", width: "36px", value: () => null, cell: (l) => <Thumb src={l.item?.photo_url} size={24} /> },
      {
        key: "sku",
        header: "SKU",
        width: "150px",
        value: (l) => l.item?.sku ?? "",
        cell: (l) => <span className="truncate font-mono text-[12px]">{l.item?.sku}</span>,
      },
      {
        key: "name",
        header: "Name",
        width: "minmax(200px,2fr)",
        value: (l) => l.item?.name ?? "",
        cell: (l) => (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate">{l.item?.name}</span>
            {l.inShopify && <Badge>Shopify</Badge>}
          </span>
        ),
      },
      {
        key: "onHand",
        header: "On hand",
        width: "90px",
        align: "right",
        value: (l) => l.onHand,
        cell: (l) => <span className={cx("font-mono text-[12px]", l.onHand < 0 && "text-danger")}>{qty(l.onHand) || "0"}</span>,
      },
      {
        key: "inTransit",
        header: "In transit",
        headerTitle: "Shipped, not yet confirmed by the CM (part of on hand)",
        width: "90px",
        align: "right",
        value: (l) => l.inTransit,
        cell: (l) => <span className="font-mono text-[12px] text-muted">{l.inTransit ? qty(l.inTransit) : ""}</span>,
      },
      {
        key: "reserved",
        header: "Reserved",
        headerTitle: "What open make orders still need, in priority order (and backers' boxes, for pre-sales)",
        width: "110px",
        align: "right",
        value: (l) => l.reserved + l.forBackers,
        cell: (l) => (
          <span
            className="font-mono text-[12px] text-muted"
            title={l.forBackers ? `${qty(l.reserved)} for open builds, ${qty(l.forBackers)} for backers' boxes (pre-sales)` : undefined}
          >
            {l.reserved || l.forBackers ? qty(l.reserved + l.forBackers) : ""}
            {l.forBackers > 0 && <span className="text-[10px] text-faint"> ·{qty(l.forBackers)} backers</span>}
          </span>
        ),
      },
      {
        key: "free",
        header: "Free",
        width: "80px",
        align: "right",
        value: (l) => l.free,
        cell: (l) => <span className={cx("font-mono text-[12px]", l.free < 0 && "text-warn")}>{qty(l.free) || "0"}</span>,
      },
      {
        key: "unitCost",
        header: "Unit cost",
        width: "90px",
        align: "right",
        value: (l) => l.unitCost,
        cell: (l) => <span className="font-mono text-[12px] text-muted">{money(l.unitCost) || <span className="text-warn">—</span>}</span>,
      },
      {
        key: "value",
        header: "Value",
        width: "100px",
        align: "right",
        value: (l) => l.value,
        cell: (l) => <span className="font-mono text-[12px]">{l.value == null ? "" : money(Math.round(l.value * 100) / 100)}</span>,
      },
      {
        key: "counted",
        header: "Counted",
        width: "100px",
        align: "right",
        value: (l) => l.lastCountedAt ?? "",
        text: (l) => l.lastCountedAt ?? "",
        cell: (l) => <span className="text-[12px] text-faint">{l.lastCountedAt ? ago(l.lastCountedAt) : l.inShopify ? "" : "never"}</span>,
      },
    ],
    [],
  );
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1">
        <DataTable
          rows={data}
          columns={columns}
          search={query}
          activeId={openItem}
          defaultSort={{ key: "value", desc: true }}
          onOpen={(l) => openPane({ kind: "item", id: l.itemId }, { root: true, replace: !!openItem })}
          rowNoun={["part", "parts"]}
          exportName="CM stock"
          bar={{ search }}
          empty={
            <div className="p-6 text-center text-muted">{query ? "No part matches." : `Nothing at ${cm.location.name} yet. Ship parts there to start.`}</div>
          }
        />
      </div>
      <div className="shrink-0 border-t border-line px-4 py-1.5 text-[12px] text-faint">
        Last count approved {cm.counts.lastCountedAt ? ago(cm.counts.lastCountedAt) : "never"}. Local parts are HHL Ops' ledger;
        {cm.location.shopify_location_id
          ? " Shopify items show Shopify's numbers (change them in Shopify)."
          : " Shopify items can't be held here (not a Shopify location)."}
      </div>
    </div>
  );
}

// Make orders -------------------------------------------------------------------------------------------------

type OrderRow = MakeOrderState & { id: string; priority: number; shortParts: MakeOrderState["components"] };

function OrdersTab({ cm, query, search }: { cm: CmSummary; query: string; search: ReactNode }) {
  const [all, setAll] = useState(false);
  const openPo = usePaneRoot("po");
  const current = cm.orders.filter((o) => o.open || o.atCm > 0);
  const finished = cm.orders.length - current.length;
  const rows = useMemo<OrderRow[]>(
    () => (all ? cm.orders : current).map((o, i) => ({ ...o, id: o.mo.id, priority: i + 1, shortParts: o.components.filter((c) => c.short > 0) })),
    [cm, all, current],
  );
  const columns = useMemo<Column<OrderRow>[]>(
    () => [
      {
        key: "priority",
        header: "#",
        headerTitle: "The order builds take parts in: the first gets what's at the CM first",
        width: "44px",
        align: "right",
        value: (o) => o.priority,
        cell: (o) => <span className="text-faint">{o.priority}</span>,
      },
      { key: "photo", header: "", width: "36px", value: () => null, cell: (o) => <Thumb src={o.item?.photo_url} size={24} /> },
      {
        key: "po",
        header: "PO",
        width: "100px",
        value: (o) => o.po.number,
        cell: (o) => <span className="font-mono text-[12px] font-medium">{o.po.number}</span>,
      },
      {
        key: "item",
        header: "Builds",
        width: "minmax(220px,2fr)",
        value: (o) => `${o.item?.sku ?? ""} ${o.item?.name ?? ""}`,
        cell: (o) => (
          <span className="truncate">
            <span className="font-mono text-[12px]">{o.item?.sku}</span> {o.item?.name}
          </span>
        ),
      },
      {
        key: "stage",
        header: "Stage",
        width: "130px",
        value: (o) => MAKE_STAGE_LABELS[o.mo.stage],
        cell: (o) => <span className="text-muted">{MAKE_STAGE_LABELS[o.mo.stage]}</span>,
      },
      {
        key: "built",
        header: "Built",
        width: "minmax(190px,1.2fr)",
        value: (o) => (o.qty ? o.output / o.qty : 0),
        text: (o) => `${o.output} of ${o.qty}`,
        cell: (o) => {
          const pct = o.qty ? Math.min(100, (o.output / o.qty) * 100) : 0;
          return (
            <span
              className="flex min-w-0 items-center gap-2"
              title={`${o.delivered ? `${qty(o.delivered)} delivered` : ""}${o.atCm ? ` · ${qty(o.atCm)} waiting at the CM` : ""}`}
            >
              <span className="h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-surface-2">
                <span className="block h-full bg-accent" style={{ width: `${pct}%` }} />
              </span>
              <span className="truncate text-[12px] text-muted">
                {qty(o.output) || "0"} of {qty(o.qty)}
                {o.atCm ? ` · ${qty(o.atCm)} at the CM` : ""}
              </span>
            </span>
          );
        },
      },
      {
        key: "status",
        header: "Status",
        width: "100px",
        value: (o) => STATUS_LABELS[o.status],
        cell: (o) => <Badge tone={STATUS_TONES[o.status]}>{STATUS_LABELS[o.status]}</Badge>,
      },
      {
        key: "buildable",
        header: "Can build now",
        width: "minmax(150px,1fr)",
        value: (o) => (o.open ? o.buildable : -1),
        text: (o) => (o.open ? o.buildable : ""),
        cell: (o) => (
          <span
            className={cx("truncate text-[12px]", o.shortParts.length ? "text-warn" : "text-muted")}
            title={o.shortParts.length ? `Short: ${o.shortParts.map((c) => `${c.sku} ${qty(c.short)}`).join(", ")}` : undefined}
          >
            {o.open ? (o.status === "ready" ? `all ${qty(o.remainingUnits)}` : `${qty(o.buildable) || "0"}`) : ""}
            {o.shortParts.length ? ` · ${plural(o.shortParts.length, "part")} short` : ""}
          </span>
        ),
      },
      {
        key: "ships",
        header: "Ships by",
        width: "100px",
        value: (o) => o.po.expected_ship ?? "",
        cell: (o) => <span className="text-[12px] text-muted">{o.po.expected_ship ? dayText(o.po.expected_ship) : ""}</span>,
      },
    ],
    [],
  );
  return (
    <div className="flex h-full min-h-0 flex-col">
      {finished > 0 && (
        <div className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-1.5 text-[12px] text-muted">
          <div className="flex rounded-md border border-line p-0.5">
            {(
              [
                [false, `Open · ${current.length}`],
                [true, `All · ${cm.orders.length}`],
              ] as const
            ).map(([value, label]) => (
              <button
                key={label}
                type="button"
                onClick={() => setAll(value)}
                className={cx("rounded px-2 py-0.5", all === value ? "bg-accent-soft font-medium text-accent-text" : "hover:text-text")}
              >
                {label}
              </button>
            ))}
          </div>
          <span>Open builds, in the order they take parts; All adds the {plural(finished, "finished build")}.</span>
        </div>
      )}
      <div className="min-h-0 flex-1">
        <DataTable
          rows={rows}
          columns={columns}
          search={query}
          activeId={openPo ? (rows.find((o) => o.po.id === openPo)?.id ?? null) : null}
          defaultSort={{ key: "priority" }}
          onOpen={(o) => openPane({ kind: "po", id: o.po.id }, { root: true, replace: !!openPo })}
          rowNoun={["build", "builds"]}
          exportName="CM make orders"
          bar={{ search }}
          empty={
            <div className="p-6 text-center text-muted">
              {query ? "No build matches." : `No open make orders at ${cm.location.name}. Make orders start on a run: open it under Runs, then its Orders.`}
            </div>
          }
        />
      </div>
    </div>
  );
}

// In transit --------------------------------------------------------------------------------------------------

type TransitRow = { id: string; kind: "shipment"; s: ShipmentState } | { id: string; kind: "po"; entry: CmSummary["inbound"][number]; vendor: string };

/** Shipments both ways (posted, not yet confirmed) and supplier POs shipping straight to the CM: one table, in two bands. */
function TransitTab({ cm, query, search }: { cm: CmSummary; query: string; search: ReactNode }) {
  const users = useRows("app_user");
  const vendors = useRows("vendor");
  const openPo = usePaneRoot("po");
  const [confirming, setConfirming] = useState<string | null>(null); // the shipment whose arrival is being confirmed
  const canReceive = can("consign.receive");
  const rows = useMemo<TransitRow[]>(
    () => [
      ...cm.inTransit.map((s) => ({ id: s.shipment.id, kind: "shipment" as const, s })),
      ...cm.inbound.map((entry) => ({
        id: entry.po.id,
        kind: "po" as const,
        entry,
        vendor: vendorLabel(vendors.find((v) => v.id === entry.po.vendor_id)),
      })),
    ],
    [cm, vendors],
  );
  const band = (r: TransitRow): TableSection =>
    r.kind === "shipment" ? { key: "shipments", label: "Shipments", order: 0 } : { key: "pos", label: "Supplier POs shipping straight there", order: 1 };
  const columns = useMemo<Column<TransitRow>[]>(
    () => [
      {
        key: "number",
        header: "No.",
        width: "120px",
        value: (r) => (r.kind === "shipment" ? r.s.shipment.number : r.entry.po.number),
        cell: (r) => (
          <span className="flex min-w-0 items-center gap-2">
            {r.kind === "po" || r.s.shipment.direction === "to_cm" ? (
              <Truck className="size-4 shrink-0 text-faint" />
            ) : (
              <Undo2 className="size-4 shrink-0 text-faint" />
            )}
            <span className="truncate font-mono text-[12px] font-medium">{r.kind === "shipment" ? r.s.shipment.number : r.entry.po.number}</span>
          </span>
        ),
      },
      {
        key: "direction",
        header: "Direction",
        width: "130px",
        value: (r) => (r.kind === "po" ? `From ${r.vendor}` : r.s.shipment.direction === "to_cm" ? "To the CM" : "Back to HHL"),
        cell: (r) => (
          <span className="truncate text-[12px] text-muted">
            {r.kind === "po" ? `From ${r.vendor}` : r.s.shipment.direction === "to_cm" ? "To the CM" : "Back to HHL"}
          </span>
        ),
      },
      {
        key: "status",
        header: "Status",
        headerTitle: "A supplier PO's status",
        width: "120px",
        value: (r) => (r.kind === "po" ? PO_STATUS_LABELS[r.entry.po.status] : ""),
        cell: (r) => (r.kind === "po" ? <Badge tone={STATUS_TONE[r.entry.po.status]}>{PO_STATUS_LABELS[r.entry.po.status]}</Badge> : null),
      },
      {
        key: "shipped",
        header: "Shipped",
        width: "110px",
        value: (r) => (r.kind === "shipment" ? r.s.shipment.shipped_at : ""),
        cell: (r) =>
          r.kind === "shipment" ? (
            <span className="truncate text-[12px] text-muted" title={new Date(r.s.shipment.shipped_at).toLocaleString()}>
              {ago(r.s.shipment.shipped_at)}
            </span>
          ) : null,
      },
      {
        key: "by",
        header: "By",
        width: "120px",
        value: (r) => (r.kind === "shipment" ? personName(users, r.s.shipment.shipped_by) : ""),
        cell: (r) => (r.kind === "shipment" ? <span className="truncate text-[12px] text-muted">{personName(users, r.s.shipment.shipped_by)}</span> : null),
      },
      {
        key: "carrier",
        header: "Carrier",
        width: "minmax(140px,1fr)",
        value: (r) => (r.kind === "shipment" ? [r.s.shipment.carrier, r.s.shipment.tracking].filter(Boolean).join(" ") : ""),
        cell: (r) =>
          r.kind === "shipment" ? (
            <span className="truncate text-[12px] text-muted">{[r.s.shipment.carrier, r.s.shipment.tracking].filter(Boolean).join(" ")}</span>
          ) : null,
      },
      {
        key: "due",
        header: "Due",
        width: "150px",
        value: (r) => (r.kind === "shipment" ? (r.s.shipment.eta ?? "") : (r.entry.po.expected_arrive ?? "")),
        cell: (r) =>
          r.kind === "shipment" ? (
            <span className="text-[12px] text-muted">{r.s.shipment.eta ? dayText(r.s.shipment.eta) : ""}</span>
          ) : (
            <span className="truncate text-[12px] text-muted">
              <NeededBy po={r.entry.po} prefix="" />
            </span>
          ),
      },
      {
        key: "parts",
        header: "Parts",
        width: "80px",
        align: "right",
        value: (r) => (r.kind === "shipment" ? r.s.lines.length : null),
        cell: (r) => (r.kind === "shipment" ? <span className="text-[12px]">{plural(r.s.lines.length, "part")}</span> : null),
      },
      {
        key: "units",
        header: "Units",
        headerTitle: "Units shipped, or for a supplier PO, units still due",
        width: "90px",
        align: "right",
        value: (r) => (r.kind === "shipment" ? r.s.units : r.entry.due),
        cell: (r) => <span className="text-[12px]">{r.kind === "shipment" ? `${qty(r.s.units)} units` : `${plural(r.entry.due, "unit")} due`}</span>,
      },
      {
        key: "value",
        header: "Value",
        width: "90px",
        align: "right",
        value: (r) => (r.kind === "shipment" ? r.s.value : null),
        cell: (r) =>
          r.kind === "shipment" ? (
            <span className="font-mono text-[12px] text-muted">{r.s.value == null ? "" : money(Math.round(r.s.value * 100) / 100)}</span>
          ) : null,
      },
      {
        key: "packing",
        header: "Packing list",
        width: "100px",
        value: () => null,
        cell: (r) =>
          r.kind === "shipment" ? (
            <a
              href={`/api/consign-shipments/${r.s.shipment.id}/packing`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[12px] text-accent-text hover:underline"
              title="The pick and packing list: each part, its bin and quantity, Picked and Packed boxes, and a QR code back to it"
            >
              Packing list
            </a>
          ) : null,
      },
      {
        key: "action",
        header: "",
        width: "140px",
        value: () => null,
        cell: (r) =>
          r.kind === "shipment"
            ? canReceive && (
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirming(r.s.shipment.id);
                  }}
                >
                  Confirm arrived
                </Button>
              )
            : can("receipt.post") && (
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/receiving?po=${r.entry.po.id}`);
                  }}
                  title="Count what the CM received, on their behalf"
                >
                  Confirm receipt
                </Button>
              ),
      },
    ],
    [users, canReceive],
  );
  const shipment = confirming ? cm.inTransit.find((s) => s.shipment.id === confirming) : undefined;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-line px-4 py-1.5 text-[12px] text-muted">
        Shipments between HHL and {cm.location.name} not yet confirmed, and supplier POs shipping straight there.
      </div>
      <div className="min-h-0 flex-1">
        <DataTable
          rows={rows}
          columns={columns}
          search={query}
          sectionOf={band}
          activeId={openPo && rows.some((r) => r.id === openPo) ? openPo : null}
          // A supplier PO opens beside the list (j/k step through them while it's open); a shipment's lines are in its
          // Confirm arrived, which opens next to that button.
          onOpen={(r) => r.kind === "po" && openPane({ kind: "po", id: r.entry.po.id }, { root: true, replace: !!openPo })}
          rowNoun={["shipment", "shipments"]}
          exportName="CM in transit"
          bar={{ search }}
          empty={<div className="p-6 text-center text-muted">{query ? "Nothing matches." : `Nothing on its way to or from ${cm.location.name}.`}</div>}
        />
      </div>
      {shipment && <ConfirmArrived s={shipment} onClose={() => setConfirming(null)} />}
    </div>
  );
}

// Counts ------------------------------------------------------------------------------------------------------

function CountsTab({ cm, vendorName, editable, open }: { cm: CmSummary; vendorName: string; editable: boolean; open: (d: Dialog) => void }) {
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px] text-muted">
          <span>Last count posted {cm.counts.lastCountedAt ? ago(cm.counts.lastCountedAt) : "never"}.</span>
          {can("location.update_cm") ? (
            <div className="w-52">
              <Select
                aria-label="Count prompt"
                value={cm.location.count_policy}
                onChange={(e) => mutate("location.update_cm", { id: cm.location.id, patch: { count_policy: e.target.value as CountPolicy } })}
                options={COUNT_POLICIES.map((p) => ({ value: p, label: `Ask for a count: ${COUNT_POLICY_LABELS[p].toLowerCase()}` }))}
                className="h-7 text-[12px]"
              />
            </div>
          ) : (
            <Badge>Count {COUNT_POLICY_LABELS[cm.location.count_policy].toLowerCase()}</Badge>
          )}
          {editable && (
            <span className="ml-auto flex gap-1.5">
              <Button size="sm" onClick={() => open("count")}>
                Enter count
              </Button>
              <Button
                size="sm"
                icon={<Printer className="size-3.5" />}
                title="A blind count sheet: every part HHL Ops counts there, with room to write what's counted (the book isn't printed)"
                onClick={() => window.open(`/api/cm/${cm.location.id}/count-sheet`, "_blank")}
              >
                Count sheet
              </Button>
            </span>
          )}
        </div>
        <div className="overflow-hidden rounded-lg border border-line">
          {cm.counts.due.due && !cm.counts.pending.length && (
            <div className="flex items-center gap-3 border-b border-line bg-warn-soft px-4 py-2 last:border-0">
              <span className="flex-1">
                Count due: {cm.counts.due.reason}. <span className="text-muted">Ask {vendorName} to count what they hold, then enter it here.</span>
              </span>
            </div>
          )}
          {cm.counts.pending.map((c) => (
            <CountRow key={c.id} id={c.id} />
          ))}
          {!cm.counts.due.due && !cm.counts.pending.length && (
            <div className="p-4 text-center text-muted">No count due and none waiting to be posted at {cm.location.name}.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function CountRow({ id }: { id: string }) {
  const count = useRows("cm_count").find((c) => c.id === id);
  return count ? <CountReview count={count} /> : null;
}
